import * as React from "react";
import { ArrowRightIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "@/lib/utils";

import { KindIcon, KINDS, type Kind } from "./kind-icon";
import { StatusIcon, type ItemStatus } from "./status-icon";

type ListGroupHeaderProps = Omit<React.ComponentProps<"button">, "title"> & {
  kind?: Kind;
  title?: React.ReactNode;
  count?: number;
  open: boolean;
};

/** Home Vertical: a collapsible 36px group header on `fill-1`. */
function ListGroupHeader({
  kind,
  title,
  count,
  open,
  className,
  ...props
}: ListGroupHeaderProps) {
  return (
    <button
      type="button"
      data-slot="list-group-header"
      aria-expanded={open}
      className={cn(
        "flex h-9 w-full min-w-0 items-center gap-2 bg-fill-1 px-3 text-left text-base text-fg outline-none hover:bg-fill-2 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset",
        className
      )}
      {...props}
    >
      <ChevronRightIcon
        className={cn(
          "size-3.5 shrink-0 text-fg-3 transition-transform duration-100",
          open && "rotate-90"
        )}
        aria-hidden
      />
      {kind ? <KindIcon kind={kind} /> : null}
      <span className="truncate font-medium">
        {title ?? (kind ? KINDS[kind].label : null)}
      </span>
      {typeof count === "number" ? (
        <span className="text-fg-3 tabular-nums">{count}</span>
      ) : null}
    </button>
  );
}

type TypeListRowProps = Omit<React.ComponentProps<"button">, "title"> & {
  status: ItemStatus;
  title: React.ReactNode;
  /** Client and visa, `fg-3`. */
  context?: React.ReactNode;
  /** 20px property chips (`Tag`, `StatusBadge`). */
  chips?: React.ReactNode;
  /** Relative time, or a danger `StatusBadge` when overdue. */
  when?: React.ReactNode;
  date?: React.ReactNode;
  assignee?: React.ReactNode;
  selected?: boolean;
};

/**
 * Home Vertical: a full-width 36px row. The fixed right columns keep time,
 * date and assignee aligned across groups.
 */
function TypeListRow({
  status,
  title,
  context,
  chips,
  when,
  date,
  assignee,
  selected = false,
  className,
  ...props
}: TypeListRowProps) {
  return (
    <button
      type="button"
      data-slot="type-list-row"
      aria-current={selected ? "true" : undefined}
      className={cn(
        "flex h-9 w-full min-w-0 items-center gap-2 border-b border-line-1 px-3 text-left text-base outline-none hover:bg-fill-1 focus-visible:bg-fill-1 aria-[current=true]:bg-fill-2",
        className
      )}
      {...props}
    >
      <StatusIcon status={status} />
      <span className="min-w-0 shrink truncate text-fg">{title}</span>
      {context ? (
        <span className="min-w-0 shrink-[2] truncate text-fg-3">{context}</span>
      ) : null}
      {chips ? (
        <span className="flex shrink-0 items-center gap-1">{chips}</span>
      ) : null}
      <span className="ml-auto flex shrink-0 items-center gap-3 text-sm text-fg-3 tabular-nums">
        <span className="flex w-20 justify-end">{when}</span>
        <span className="w-16 text-right">{date}</span>
        <span className="flex w-5 justify-end">{assignee}</span>
      </span>
    </button>
  );
}

type TypeColumnProps = Omit<React.ComponentProps<"section">, "title"> & {
  kind: Kind;
  count?: number;
  /** The header arrow: switch to Vertical scrolled to this group. */
  onOpenVertical?: () => void;
};

/**
 * Home Horizontal: one agenda column per type. Rows are plain (when, title,
 * client): no cards, no status circles, nothing to drag. Each column scrolls
 * on its own; the parent divides columns with `divide-x divide-line-2`.
 */
function TypeColumn({
  kind,
  count,
  onOpenVertical,
  className,
  children,
  ...props
}: TypeColumnProps) {
  return (
    <section
      data-slot="type-column"
      className={cn("flex min-h-0 min-w-0 flex-1 flex-col", className)}
      {...props}
    >
      <header className="flex h-9 shrink-0 items-center gap-2 border-b border-line-1 px-3">
        <KindIcon kind={kind} />
        <span className="truncate text-base font-medium text-fg">
          {KINDS[kind].label}
        </span>
        {typeof count === "number" ? (
          <span className="text-base text-fg-3 tabular-nums">{count}</span>
        ) : null}
        {onOpenVertical ? (
          <button
            type="button"
            onClick={onOpenVertical}
            aria-label={`Open ${KINDS[kind].label} in Vertical`}
            className="ml-auto flex size-5 items-center justify-center rounded-xs text-fg-3 hover:bg-fill-1 hover:text-fg"
          >
            <ArrowRightIcon className="size-3.25" />
          </button>
        ) : null}
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {children}
      </div>
    </section>
  );
}

type AgendaRowProps = Omit<React.ComponentProps<"button">, "title"> & {
  when: React.ReactNode;
  title: React.ReactNode;
  client?: React.ReactNode;
};

/** A plain Horizontal row: when line, title, client. */
function AgendaRow({
  when,
  title,
  client,
  className,
  ...props
}: AgendaRowProps) {
  return (
    <button
      type="button"
      data-slot="agenda-row"
      className={cn(
        "flex w-full min-w-0 flex-col gap-0.5 border-b border-line-1 px-3 py-2 text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1",
        className
      )}
      {...props}
    >
      <span className="text-sm text-fg-3 tabular-nums">{when}</span>
      <span className="truncate text-base text-fg">{title}</span>
      {client ? (
        <span className="truncate text-sm text-fg-3">{client}</span>
      ) : null}
    </button>
  );
}

export { AgendaRow, ListGroupHeader, TypeColumn, TypeListRow };
export type {
  AgendaRowProps,
  ListGroupHeaderProps,
  TypeColumnProps,
  TypeListRowProps
};
